import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  DataTable,
  BreadcrumbItem,
  astryx,
  neutralTheme,
  Plus,
  Search,
  UsersRound,
  useDocumentTheme,
  defaultPagination,
  type IPagination,
} from "@shadowcue/ui";

interface UsersIndexProps {
  currentUser: IUser;
}

// Declared as a type alias (not an interface) so it satisfies Astryx
// Table's `T extends Record<string, unknown>` generic constraint — plain
// interfaces don't structurally match an index-signature constraint.
type IUserRecord = {
  id: number;
  email: string;
};

const axios = newAxiosClient();

const DEBOUNCE_DURATION = 500;

const UsersIndex = (props: UsersIndexProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<IUserRecord[]>([]);
  const [pagination, setPagination] =
    useState<IPagination>(defaultPagination());
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>(searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(
    searchParams.get("search") ?? "",
  );

  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Users", href: "/admin/users" },
  ];

  const fetchUsers = async (page: number = pagination.page) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/users", {
        headers: {
          AUTHORIZATION: `Bearer ${props.currentUser.authToken}`,
        },
        params: {
          page,
          limit: pagination.limit,
          search: debouncedSearch || undefined,
        },
      });
      setUsers(res.data.users || []);
      setPagination(res.data.pagination || defaultPagination());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search),
      DEBOUNCE_DURATION,
    );
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (debouncedSearch.trim()) {
      nextParams.set("search", debouncedSearch.trim());
    } else {
      nextParams.delete("search");
    }
    setSearchParams(nextParams, { replace: true });

    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const isNarrowing = debouncedSearch.trim() !== "";
  const clearSearch = () => setSearch("");

  const columns: astryx.TableColumn<IUserRecord>[] = [
    {
      key: "email",
      header: "Email",
      width: astryx.proportional(1),
    },
    {
      key: "actions",
      header: "Actions",
      width: astryx.pixel(100),
      renderCell: (user) => (
        <astryx.Button
          label="Edit"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/users/${user.id}/edit`)}
        />
      ),
    },
  ];

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={false}>
          <PageHeader
            title="Users"
            description="Manage user accounts in your organization."
            action={
              <astryx.Button
                label="New user"
                variant="primary"
                icon={<astryx.Icon icon={Plus} />}
                onClick={() => navigate("/admin/users/new")}
              />
            }
          />

          <astryx.VStack gap={6}>
            <astryx.HStack gap={3} align="center" wrap="wrap">
              <astryx.StackItem size="fill">
                <astryx.TextInput
                  label="Search users"
                  isLabelHidden
                  startIcon={<astryx.Icon icon={Search} />}
                  placeholder="Search by email…"
                  hasClear
                  width="100%"
                  value={search}
                  onChange={(value: string) => setSearch(value)}
                />
              </astryx.StackItem>
            </astryx.HStack>

            <DataTable
              loading={loading}
              data={users}
              columns={columns}
              idKey="id"
              emptyState={
                isNarrowing
                  ? {
                      icon: <astryx.Icon icon={UsersRound} />,
                      headline: "No users match",
                      subtext: "Try a different search.",
                      action: (
                        <astryx.Button
                          label="Clear search"
                          onClick={clearSearch}
                        />
                      ),
                    }
                  : {
                      icon: <astryx.Icon icon={UsersRound} />,
                      headline: "No users yet",
                      subtext: "Add your first user to get started.",
                      action: (
                        <astryx.Button
                          label="Add user"
                          variant="primary"
                          onClick={() => navigate("/admin/users/new")}
                        />
                      ),
                    }
              }
              pagination={{
                page: pagination.page,
                totalPages: pagination.pages ?? 1,
                onChange: (page: number) => fetchUsers(page),
              }}
            />
          </astryx.VStack>
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default UsersIndex;
